import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { api } from "@/lib/api";
import { Note } from "@/types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Markdown } from "@/components/ui/markdown";
import { MarkdownField } from "@/components/ui/markdown-field";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { AppLayout } from "@/components/layout/AppLayout";

export default function NotePage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [note, setNote] = useState<Note | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedNote, setEditedNote] = useState<Partial<Note>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchNote();
    }
  }, [id]);

  const fetchNote = async () => {
    try {
      setIsLoading(true);
      const response = await api(`/notes/${id}`);
      setNote(response as Note);
      setEditedNote(response as Note);
    } catch (error) {
      console.error("Error fetching note:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!note) return;

    try {
      setIsSaving(true);
      const response = await api(`/notes/${id}`, {
        method: "PUT",
        body: editedNote,
      });
      setNote(response as Note);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating note:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!note || !confirm("Are you sure you want to delete this note?")) return;

    try {
      await api(`/notes/${id}`, {
        method: "DELETE",
      });
      navigate("/notes");
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="container max-w-4xl mx-auto py-8 px-6">
          <div className="text-center text-muted-foreground">Loading note...</div>
        </div>
      </AppLayout>
    );
  }

  if (!note) {
    return (
      <AppLayout>
        <div className="container max-w-4xl mx-auto py-8 px-6">
          <div className="text-center text-muted-foreground">Note not found</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="container max-w-4xl mx-auto py-8 px-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/notes")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Note</h1>
        </div>

        <Card className="p-6">
          {isEditing ? (
            <div className="space-y-4">
              <MarkdownField
                placeholder="Note content..."
                value={editedNote.content ?? ""}
                onValueChange={(content) => setEditedNote({ ...editedNote, content })}
                className="min-h-[200px] max-h-[28rem]"
                previewClassName="min-h-[200px]"
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setEditedNote(note);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Markdown content={note.content} />
              <div className="text-sm text-muted-foreground">
                Created {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AppLayout>
  );
} 